import { create } from 'zustand';
import type { Channel } from '@shared/types.ts';

/**
 * The ordered list the current channel came from.
 *
 * "Next channel" is meaningless without it: the player knows what is playing,
 * not what is beside it. Rather than teach the player controller about lists
 * — it deliberately knows nothing about where channels come from — the grid
 * hands over the lineup it was showing when the user pressed a channel.
 *
 * That also makes channel-cycling match what you were BROWSING, which is the
 * behaviour people expect: cycle through Live TV if you tuned from Live TV,
 * even though the radio stations exist too.
 */
interface LineupState {
  channels: Channel[];
  set(channels: Channel[]): void;
}

export const useLineup = create<LineupState>((set) => ({
  channels: [],
  set: (channels) => set({ channels }),
}));

/** Step `delta` places from `currentId`, wrapping. Null when there's no list. */
export function stepChannel(currentId: string | null, delta: number): Channel | null {
  const { channels } = useLineup.getState();
  if (channels.length === 0) return null;

  const i = currentId ? channels.findIndex((c) => c.id === currentId) : -1;
  if (i < 0) return channels[0];

  // Wrap, because a remote pressed twice at the end of a list should keep
  // working rather than silently stop.
  const next = (i + delta + channels.length) % channels.length;
  return channels[next] ?? null;
}
